import { absenceTooltip } from '../../lib/absence-tooltip'
import type {
  Absence,
  AbsenceType,
  Break,
  BreakRuleTier,
  Nonbusinessday,
  TimeEntry,
  WorkSchedule,
} from '../../lib/api'
import {
  addDays,
  dayKey,
  endOfMonth,
  isoWeek,
  isSameDay,
  startOfMonth,
  startOfWeek,
} from '../../lib/calendar'
import { displayAbsenceForDay, metricsForDay } from '../../lib/calendar-overlays'
import { cn, formatHM } from '../../lib/utils'

const ABSENCE_COLOR: Record<AbsenceType, string> = {
  vacation: '#3B82F6',
  sick: '#EF4444',
  training: '#10B981',
  other: '#A3978C',
  overtime_reduction: '#A3978C',
  maternity: '#A3978C',
  parental: '#A3978C',
}
const ABSENCE_LABEL: Record<AbsenceType, string> = {
  vacation: 'Urlaub',
  sick: 'Krank',
  training: 'Fortbildung',
  other: 'Frei',
  overtime_reduction: 'Abbau',
  maternity: 'Mutterschutz',
  parental: 'Elternzeit',
}

const MONTH_LABELS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
]
const WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

interface YearViewProps {
  date: Date
  entries: TimeEntry[]
  absences?: Absence[]
  holidays?: Map<string, Nonbusinessday>
  breaks?: Break[]
  breakTiers?: BreakRuleTier[]
  targetHours?: readonly WorkSchedule[]
  meetings?: { startTime: string; endTime: string; countsAsWork?: boolean }[]
  onDayClick: (day: Date) => void
}

const EMPTY_ABSENCES: Absence[] = []
const EMPTY_HOLIDAYS = new Map<string, Nonbusinessday>()
const EMPTY_BREAKS: Break[] = []
const EMPTY_TIERS: BreakRuleTier[] = []
const EMPTY_TARGETS: readonly WorkSchedule[] = []
const EMPTY_MEETINGS: { startTime: string; endTime: string }[] = []

export function YearView({
  date,
  entries,
  absences = EMPTY_ABSENCES,
  holidays = EMPTY_HOLIDAYS,
  breaks = EMPTY_BREAKS,
  breakTiers = EMPTY_TIERS,
  targetHours = EMPTY_TARGETS,
  meetings = EMPTY_MEETINGS,
  onDayClick,
}: YearViewProps) {
  const year = date.getFullYear()

  // Aggregate worked minutes per day-key once (entries clipped to each day).
  const workedByDay = new Map<string, number>()
  for (const e of entries) {
    const start = new Date(e.startTime).getTime()
    const end = e.endTime ? new Date(e.endTime).getTime() : Date.now()
    const startDay = new Date(e.startTime)
    startDay.setHours(0, 0, 0, 0)
    let cursor = startDay.getTime()
    while (cursor < end) {
      const dayEnd = cursor + 86_400_000
      const segE = Math.min(end, dayEnd)
      const segS = Math.max(start, cursor)
      if (segE > segS) {
        const k = dayKey(new Date(cursor))
        workedByDay.set(k, (workedByDay.get(k) ?? 0) + (segE - segS) / 60_000)
      }
      cursor = dayEnd
    }
  }

  const today = new Date()
  const months = Array.from({ length: 12 }, (_, m) => new Date(year, m, 1))

  // Yearly summary
  const totalMin = Array.from(workedByDay.values()).reduce((a, b) => a + b, 0)
  const activeDays = Array.from(workedByDay.values()).filter((v) => v > 0).length
  const absenceCounts = absences.reduce<Record<AbsenceType, number>>(
    (acc, a) => {
      // Nur GENEHMIGTE Abwesenheiten zählen — pending erscheint im Raster
      // gestreift, aber die Kopf-Legende soll die wirksamen Tage nennen
      // (vorher zählten auch abgelehnte/zurückgezogene Anträge mit).
      if (a.status !== 'approved') return acc
      // Count distinct days inside the year that this absence covers.
      const from = new Date(a.fromDate)
      const to = new Date(a.toDate)
      from.setHours(0, 0, 0, 0)
      to.setHours(0, 0, 0, 0)
      let d = from.getTime()
      while (d <= to.getTime()) {
        const dt = new Date(d)
        if (dt.getFullYear() === year) acc[a.type] = (acc[a.type] ?? 0) + 1
        d += 86_400_000
      }
      return acc
    },
    {
      vacation: 0,
      sick: 0,
      training: 0,
      other: 0,
      overtime_reduction: 0,
      maternity: 0,
      parental: 0,
    },
  )

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-text-tertiary">Jahr {year}</p>
          <p className="font-mono text-2xl font-light text-accent tabular-nums">
            {formatHM(Math.round(totalMin))}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <span className="text-text-tertiary">
            {activeDays === 1 ? '1 aktiver Tag' : `${activeDays} aktive Tage`}
          </span>
          {(['vacation', 'sick', 'training', 'other'] as AbsenceType[]).map((t) =>
            absenceCounts[t] > 0 ? (
              <span
                key={t}
                className="rounded px-1.5 py-0.5"
                style={{
                  backgroundColor: `${ABSENCE_COLOR[t]}26`,
                  color: ABSENCE_COLOR[t],
                }}
              >
                {absenceCounts[t]} {ABSENCE_LABEL[t]}
              </span>
            ) : null,
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {months.map((m) => (
          <MonthCard
            key={m.toISOString()}
            month={m}
            today={today}
            workedByDay={workedByDay}
            entries={entries}
            absences={absences}
            holidays={holidays}
            breaks={breaks}
            breakTiers={breakTiers}
            targetHours={targetHours}
            meetings={meetings}
            onDayClick={onDayClick}
          />
        ))}
      </div>
    </div>
  )
}

interface MonthCardProps {
  month: Date
  today: Date
  workedByDay: Map<string, number>
  entries: TimeEntry[]
  absences: Absence[]
  holidays: Map<string, Nonbusinessday>
  breaks: Break[]
  breakTiers: BreakRuleTier[]
  targetHours: readonly WorkSchedule[]
  meetings: { startTime: string; endTime: string }[]
  onDayClick: (day: Date) => void
}

function MonthCard({
  month,
  today,
  workedByDay,
  entries,
  absences,
  holidays,
  breaks,
  breakTiers,
  targetHours,
  meetings,
  onDayClick,
}: MonthCardProps) {
  const monthStart = startOfMonth(month)
  const monthEnd = endOfMonth(month)
  const gridStart = startOfWeek(monthStart)
  const cells: Date[] = []
  let cur = new Date(gridStart)
  while (cur <= monthEnd || cells.length % 7 !== 0) {
    cells.push(new Date(cur))
    cur = addDays(cur, 1)
    if (cells.length > 42) break
  }
  const weeks: Date[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))

  // KW span for the month subtitle (e.g. "KW 18 – 22"). Use first/last
  // in-month days, not the gridStart cells which can spill into adjacent
  // months.
  const inMonthDays = cells.filter((d) => d.getMonth() === month.getMonth())
  const kwFirst = isoWeek(inMonthDays[0])
  const kwLast = isoWeek(inMonthDays[inMonthDays.length - 1])
  const kwLabel = kwFirst === kwLast ? `KW ${kwFirst}` : `KW ${kwFirst}–${kwLast}`

  // Layout: 14px gutter for unobtrusive week numbers + 7 day columns
  const gridStyle = { gridTemplateColumns: '14px repeat(7, minmax(0, 1fr))' } as const

  // Per-month Soll/Ist aggregate — only in-month days, only past + today.
  // Future days of the CURRENT year must be skipped: they carry a full
  // targetMinutes but zero actuals, which would show the running month as a
  // phantom deficit (e.g. −160:00 in early July). Mirrors the backend YTD-cap
  // `if (isCurrentYear && date > todayUtc) continue` in time-accounts.ts.
  const todayEnd = new Date()
  todayEnd.setHours(23, 59, 59, 999)
  const isCurrentYear = month.getFullYear() === todayEnd.getFullYear()
  let monthTarget = 0
  let monthActual = 0
  for (const d of inMonthDays) {
    if (isCurrentYear && d.getTime() > todayEnd.getTime()) continue
    const m = metricsForDay(
      d,
      entries,
      breaks,
      breakTiers,
      holidays,
      absences,
      targetHours,
      meetings,
    )
    monthTarget += m.targetMinutes
    monthActual += m.totalActualMinutes
  }
  const monthDiff = monthActual - monthTarget
  const monthHasTarget = monthTarget > 0
  const monthNeutral = Math.abs(monthDiff) < 15

  return (
    <div className="rounded-xl border border-border-subtle bg-bg-elevated/40 p-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium tracking-tight">{MONTH_LABELS[month.getMonth()]}</p>
        <div className="flex items-center gap-2">
          {monthHasTarget && (
            <span
              className={cn(
                'font-mono tabular-nums text-2xs rounded px-1',
                monthNeutral && 'text-text-tertiary',
                !monthNeutral && monthDiff >= 0 && 'text-success bg-success/10',
                !monthNeutral && monthDiff < 0 && 'text-danger bg-danger/10',
              )}
              title={`Soll ${formatHM(monthTarget)} · Ist ${formatHM(monthActual)}`}
            >
              {monthDiff >= 0 ? '+' : '−'}
              {formatHM(Math.abs(monthDiff))}
            </span>
          )}
          <p className="font-mono tabular-nums text-2xs text-text-tertiary">{kwLabel}</p>
        </div>
      </div>
      <div className="grid gap-px text-2xs text-text-tertiary mb-1" style={gridStyle}>
        <div />
        {WEEKDAY_LABELS.map((wd) => (
          <div key={wd} className="text-center">
            {wd}
          </div>
        ))}
      </div>
      <div className="grid gap-px" style={gridStyle}>
        {weeks.flatMap((week) => [
          <div
            key={`kw-${dayKey(week[0])}`}
            className="flex items-center justify-center text-2xs font-mono tabular-nums text-text-tertiary/60"
            title={`Kalenderwoche ${isoWeek(week[0])}`}
          >
            {isoWeek(week[0])}
          </div>,
          ...week.map((day) => {
            const inMonth = day.getMonth() === month.getMonth()
            const isToday = isSameDay(day, today)
            const k = dayKey(day)
            const workedMin = workedByDay.get(k) ?? 0
            const dayDisplay = inMonth ? displayAbsenceForDay(day, absences) : null
            const absence = dayDisplay?.absence
            const absencePending = dayDisplay?.pending ?? false
            const holiday = inMonth ? (holidays.get(k) ?? null) : null

            // Detect whether this specific day is a half-day within the
            // absence range. Used for the "½" badge + a half-height bg fill.
            const isHalfAbsence = (() => {
              if (!absence) return false
              const sameStart = k === absence.fromDate.slice(0, 10)
              const sameEnd = k === absence.toDate.slice(0, 10)
              const sameSingle = absence.fromDate.slice(0, 10) === absence.toDate.slice(0, 10)
              if (sameSingle && sameStart) return !!absence.firstDayHalf
              if (sameStart && absence.firstDayHalf) return true
              if (sameEnd && absence.lastDayHalf) return true
              return false
            })()

            let bg = 'transparent'
            let fg = 'rgb(var(--color-text-tertiary))'
            if (!inMonth) {
              bg = 'transparent'
            } else if (absence) {
              // pending: halbe Deckkraft — wirksam wird der Tag erst mit
              // Genehmigung (Streifen sind in der Mini-Zelle zu fein).
              bg = absencePending
                ? `${ABSENCE_COLOR[absence.type]}1A`
                : `${ABSENCE_COLOR[absence.type]}33`
              fg = ABSENCE_COLOR[absence.type]
            } else if (holiday) {
              bg = 'rgba(245, 158, 11, 0.18)' // warning tint
              fg = 'rgb(245, 158, 11)'
            } else if (workedMin > 0) {
              // Accent intensity: 0..8h → 0.12..0.45 alpha
              const i = Math.min(1, workedMin / 480)
              bg = `rgba(255, 107, 53, ${(0.12 + i * 0.33).toFixed(2)})`
              fg = 'rgb(var(--color-text-primary))'
            } else {
              bg = 'rgba(245, 240, 235, 0.03)'
              fg = 'rgb(var(--color-text-secondary))'
            }

            const tooltipParts: string[] = [
              day.toLocaleDateString('de-DE', {
                weekday: 'short',
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              }),
            ]
            if (absence) {
              // Rich tooltip line — type + status, notes, substitute, approval.
              // absenceTooltip emits multiple lines separated by \n; we splice
              // them straight into tooltipParts so the year-view's tooltip
              // shows the full picture on hover (kept inline so the existing
              // .join(' · ') for the cell title still works).
              const richLines = absenceTooltip(absence).split('\n').filter(Boolean)
              tooltipParts.push(...richLines)
            }
            if (holiday) tooltipParts.push(`Feiertag: ${holiday.name}`)
            if (workedMin > 0) tooltipParts.push(`${formatHM(Math.round(workedMin))} gearbeitet`)

            // Compute warning state per cell. Mirrors the badges shown in the
            // WeekFooter: red for hard violations (>10h, >6h ohne Pause,
            // Einträge trotz Abwesenheit), yellow for break-deficit only.
            let warning: 'red' | 'yellow' | null = null
            let warningTitle = ''
            if (inMonth) {
              const m = metricsForDay(
                day,
                entries,
                breaks,
                breakTiers,
                holidays,
                absences,
                targetHours,
                meetings,
              )
              if (m.entriesOnAbsence) {
                warning = 'red'
                warningTitle = 'Zeiteinträge trotz Abwesenheit'
              } else if (m.unbrokenStretchViolation) {
                warning = 'red'
                warningTitle = `${formatHM(m.longestRunMinutes)} ohne Pause`
              } else if (m.workedMinutes > 10 * 60 && !m.absence) {
                warning = 'red'
                warningTitle = `${formatHM(m.workedMinutes)} — über 10h Arbeit`
              } else if (m.breakDeficitMinutes > 0) {
                warning = 'yellow'
                warningTitle = `${m.breakDeficitMinutes}m Pause fehlt`
              }
              if (warning) tooltipParts.push(`⚠️ ${warningTitle}`)
            }

            // For half-day absences, paint only one half with the absence
            // tint so the tile actually looks "half occupied". We let the base
            // bg fall through to transparent / worked tint and overlay the
            // half on top via a child div.
            const halfOverlayBg =
              isHalfAbsence && absence ? `${ABSENCE_COLOR[absence.type]}55` : null
            const halfOverlayPos =
              isHalfAbsence && absence
                ? absence.firstDayHalf === 'morning' || absence.lastDayHalf === 'morning'
                  ? 'top-0 h-1/2'
                  : 'bottom-0 h-1/2'
                : null

            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => inMonth && onDayClick(day)}
                disabled={!inMonth}
                title={inMonth ? tooltipParts.join(' · ') : ''}
                className={cn(
                  'relative aspect-square rounded-sm flex flex-col items-center justify-center transition-all overflow-hidden',
                  'text-2xs leading-none font-mono tabular-nums',
                  inMonth ? 'cursor-pointer hover:scale-110 hover:z-10 hover:ring-1' : 'opacity-30',
                  isToday && 'ring-1 ring-accent',
                )}
                style={{
                  backgroundColor: isHalfAbsence ? 'transparent' : bg,
                  color: fg,
                }}
              >
                {halfOverlayBg && halfOverlayPos && (
                  <span
                    aria-hidden
                    className={cn('absolute inset-x-0 pointer-events-none', halfOverlayPos)}
                    style={{ backgroundColor: halfOverlayBg }}
                  />
                )}
                <span className={cn('relative', isToday && 'font-semibold')}>{day.getDate()}</span>
                {inMonth && workedMin > 0 && !absence && (
                  <span className="relative text-2xs leading-none opacity-80">
                    {formatShortHours(workedMin)}
                  </span>
                )}
                {isHalfAbsence && (
                  <span
                    title="Halber Tag"
                    className="absolute bottom-0 left-0 text-2xs font-bold leading-none px-0.5 opacity-90"
                  >
                    ½
                  </span>
                )}
                {warning && (
                  <span
                    role="img"
                    aria-label={warningTitle}
                    className={cn(
                      'absolute top-0 right-0 h-1.5 w-1.5 rounded-full ring-1 ring-bg-elevated',
                      warning === 'red' ? 'bg-danger' : 'bg-warning',
                    )}
                  />
                )}
              </button>
            )
          }),
        ])}
      </div>
    </div>
  )
}

function formatShortHours(minutes: number): string {
  const h = minutes / 60
  if (h >= 10) return `${Math.round(h)}h`
  if (h >= 1) return `${h.toFixed(1).replace('.0', '')}h`
  return `${Math.round(minutes)}m`
}
